<script setup>
import { reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});
const emit = defineEmits(['submit']);
const BUILTIN_KEYS = [
  'lost_interest',
  'price',
  'photo',
  'offer',
  'browsing',
  'missing',
];
const MAX_CUSTOM = 10;

const { t } = useI18n();

const state = reactive({
  plays: [],
});

const customCount = computed(
  () => state.plays.filter(play => !BUILTIN_KEYS.includes(play.key)).length
);

const playCopy = key => {
  const name = key.toUpperCase();
  return {
    title: t(`CAPTAIN.BRAIN.MARKETING.PLAYS.${name}.TITLE`),
    goal: t(`CAPTAIN.BRAIN.MARKETING.PLAYS.${name}.GOAL`),
    action: t(`CAPTAIN.BRAIN.MARKETING.PLAYS.${name}.ACTION`),
    avoid: t(`CAPTAIN.BRAIN.MARKETING.PLAYS.${name}.AVOID`),
  };
};

const keep = (value, fallback = '') =>
  value === undefined || value === null ? fallback : value;

const blankPlay = (key, saved = {}) => {
  const defaults = BUILTIN_KEYS.includes(key) ? playCopy(key) : {};
  return {
    key,
    enabled: saved.enabled !== false,
    title: keep(saved.title, defaults.title || ''),
    trigger: keep(saved.trigger, ''),
    goal: keep(saved.goal, defaults.goal || ''),
    action: keep(saved.action, defaults.action || ''),
    avoid: keep(saved.avoid, defaults.avoid || ''),
  };
};

const updateStateFromAssistant = assistant => {
  const stored = Array.isArray(assistant?.config?.marketing?.plays)
    ? assistant.config.marketing.plays
    : [];
  const byKey = Object.fromEntries(
    stored.filter(play => play?.key).map(play => [play.key, play])
  );
  const builtins = BUILTIN_KEYS.map(key => blankPlay(key, byKey[key] || {}));
  const customs = stored
    .filter(play => play?.key && !BUILTIN_KEYS.includes(play.key))
    .slice(0, MAX_CUSTOM)
    .map(play => blankPlay(play.key, play));
  state.plays = [...builtins, ...customs];
};

const addCustom = () => {
  if (customCount.value >= MAX_CUSTOM) return;
  state.plays.push(
    blankPlay(`custom_${Date.now()}`, {
      enabled: true,
      title: '',
      goal: '',
      action: '',
      avoid: '',
    })
  );
};

const removePlay = key => {
  state.plays = state.plays.filter(play => play.key !== key);
};

const handleSubmit = () => {
  emit('submit', {
    config: {
      marketing: {
        plays: state.plays
          .slice(0, BUILTIN_KEYS.length + MAX_CUSTOM)
          .map(play => ({
            key: play.key,
            enabled: play.enabled !== false,
            title: play.title,
            trigger: play.trigger,
            goal: play.goal,
            action: play.action,
            avoid: play.avoid,
          })),
      },
    },
  });
};

watch(
  () => props.assistant,
  assistant => {
    if (assistant) updateStateFromAssistant(assistant);
  },
  { immediate: true }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <p class="mb-0 text-sm text-n-slate-11">
      {{ t('CAPTAIN.BRAIN.MARKETING.HELP') }}
    </p>

    <section
      v-for="play in state.plays"
      :key="play.key"
      class="flex flex-col gap-3 rounded-xl outline outline-1 outline-n-weak p-4"
    >
      <div class="flex items-center justify-between gap-3">
        <h4 class="mb-0 text-base font-medium text-n-slate-12">
          {{
            BUILTIN_KEYS.includes(play.key)
              ? t(
                  `CAPTAIN.BRAIN.MARKETING.PLAYS.${play.key.toUpperCase()}.TITLE`
                )
              : play.title || t('CAPTAIN.BRAIN.MARKETING.CUSTOM')
          }}
        </h4>
        <Switch v-model="play.enabled" />
      </div>
      <p
        v-if="BUILTIN_KEYS.includes(play.key)"
        class="mb-0 text-xs text-n-slate-11"
      >
        {{ t(`CAPTAIN.BRAIN.MARKETING.PLAYS.${play.key.toUpperCase()}.HELP`) }}
      </p>
      <Input
        v-if="!BUILTIN_KEYS.includes(play.key)"
        v-model="play.title"
        :label="t('CAPTAIN.BRAIN.MARKETING.FIELDS.TITLE')"
      />
      <Input
        v-model="play.trigger"
        :label="t('CAPTAIN.BRAIN.MARKETING.FIELDS.TRIGGER')"
        :placeholder="t('CAPTAIN.BRAIN.MARKETING.FIELDS.TRIGGER_PLACEHOLDER')"
      />
      <Input
        v-model="play.goal"
        :label="t('CAPTAIN.BRAIN.MARKETING.FIELDS.GOAL')"
      />
      <TextArea
        v-model="play.action"
        :label="t('CAPTAIN.BRAIN.MARKETING.FIELDS.ACTION')"
        :max-length="400"
        auto-height
      />
      <TextArea
        v-model="play.avoid"
        :label="t('CAPTAIN.BRAIN.MARKETING.FIELDS.AVOID')"
        :max-length="240"
        auto-height
      />
      <div v-if="!BUILTIN_KEYS.includes(play.key)" class="flex justify-end">
        <Button
          type="button"
          ghost
          ruby
          xs
          :label="t('CAPTAIN.BRAIN.MARKETING.REMOVE')"
          @click="removePlay(play.key)"
        />
      </div>
    </section>

    <div class="flex flex-wrap items-center justify-between gap-3">
      <Button
        type="button"
        ghost
        slate
        :label="t('CAPTAIN.BRAIN.MARKETING.ADD')"
        :disabled="customCount >= MAX_CUSTOM"
        @click="addCustom"
      />
      <Button type="submit" :label="t('CAPTAIN.BRAIN.MARKETING.SAVE')" />
    </div>
  </form>
</template>
